import { Loader2, Shield, UserPlus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { ConfirmModal } from '@/components/ui/modal'
import { isApiError } from '@/lib/api/errors'
import { parseUserRoleReassignmentWarning, useAssignRoleUsers, usePermissionCatalog } from '../api'
import type { Role, UserRoleReassignmentWarning } from '../types'
import { toastError } from '../ui'
import { useAccessLookups } from '../useLookups'
import { FormDrawer } from './FormDrawer'
import { ScopeBadge } from './AccessBits'
import { UserPicker } from './UserPicker'

export function AssignUsersDialog({ role, onOpenChange }: { role: Role | null; onOpenChange: (v: boolean) => void }) {
  return role ? <AssignUsersForm key={role.id} role={role} onClose={() => onOpenChange(false)} /> : null
}

function AssignUsersForm({ role, onClose }: { role: Role; onClose: () => void }) {
  const { t } = useTranslation()
  const { users, departmentById } = useAccessLookups()
  const catalog = usePermissionCatalog().data
  const assign = useAssignRoleUsers()
  const [selected, setSelected] = useState<string[]>(() => role.assignments.map((a) => a.userId))
  const [pendingConfirm, setPendingConfirm] = useState<UserRoleReassignmentWarning | null>(null)

  const selectedUsers = useMemo(() => users.filter((u) => selected.includes(u.id)), [users, selected])
  const permissionCount = role.permissions.length
  const moduleBuckets = useMemo(() => {
    const groups = catalog?.groups ?? []
    const counts = new Map<string, { count: number; label: string }>()
    for (const item of role.permissionItems.length ? role.permissionItems : role.permissions.map((code) => ({ code, module: code.split(/[:.]/)[0] || 'general' }))) {
      const moduleKey = item.module || item.code.split(/[:.]/)[0] || 'general'
      const group = groups.find((g) => g.key === moduleKey || g.permissions.some((p) => p.code === item.code))
      const key = group?.key || moduleKey
      const prev = counts.get(key)
      counts.set(key, { count: (prev?.count ?? 0) + 1, label: group?.title || key })
    }
    return [...counts.entries()]
      .map(([key, value]) => ({ key, count: value.count, label: value.label }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4)
  }, [role.permissionItems, role.permissions, catalog])

  const submit = async (confirm: boolean) => {
    try {
      await assign.mutateAsync({ id: role.id, userIds: selected, confirmUserReassignment: confirm })
      toast.success(t('access.users.assigned', { count: selected.length }))
      setPendingConfirm(null)
      onClose()
    } catch (e) {
      if (!confirm && isApiError(e) && e.status === 409 && e.code === 'USER_ROLE_REASSIGNMENT_REQUIRES_CONFIRMATION') {
        const warning = parseUserRoleReassignmentWarning(e.warning)
        if (warning) {
          setPendingConfirm(warning)
          return
        }
      }
      toastError(e)
    }
  }

  return (
    <>
      <FormDrawer
        open
        onOpenChange={(v) => !v && onClose()}
        size="lg"
        icon={<UserPlus />}
        title={t('access.users.assignTitle', { role: role.name })}
        description={t('access.users.assignHint')}
        footer={
          <>
            <Button variant="cancel" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button disabled={assign.isPending} onClick={() => void submit(false)}>
              {assign.isPending ? <Loader2 className="animate-spin" /> : null}
              {t('access.users.assignSubmit', { count: selected.length })}
            </Button>
          </>
        }
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">{t('access.users.pickTitle')}</h3>
            <UserPicker users={users} departmentById={departmentById} value={selected} onChange={setSelected} />
            {selectedUsers.length ? (
              <div className="rounded-xl border p-3">
                <p className="mb-2 text-xs font-medium text-muted-foreground">{t('access.users.selectedTitle', { count: selectedUsers.length })}</p>
                <ul className="space-y-2">
                  {selectedUsers.slice(0, 5).map((u) => (
                    <li key={u.id} className="flex items-center gap-2">
                      <Avatar name={u.name} className="size-7" />
                      <span className="min-w-0 flex-1 truncate text-sm">{u.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">{t('access.users.previewTitle')}</h3>
            <div className="space-y-3 rounded-xl border p-3">
              <div>
                <p className="text-xs text-muted-foreground">{t('access.users.assignRole')}</p>
                <p className="mt-1 font-medium">{role.name}</p>
                <div className="mt-2">
                  <ScopeBadge scope={role.scope} />
                </div>
              </div>
              <div>
                <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Shield className="size-3.5" />
                  {t('access.users.effectivePermissions', { count: permissionCount })}
                </p>
                <ul className="space-y-1.5">
                  {moduleBuckets.map((b) => (
                    <li key={b.key} className="flex items-center justify-between rounded-lg bg-muted/50 px-2.5 py-1.5 text-sm">
                      <span>{b.label}</span>
                      <span className="tabular-nums text-muted-foreground">{b.count}</span>
                    </li>
                  ))}
                  {moduleBuckets.length === 0 ? <li className="text-sm text-muted-foreground">{t('access.users.noPermissions')}</li> : null}
                </ul>
              </div>
              <p className="rounded-lg border border-brand-blue/20 bg-brand-blue/[0.06] px-3 py-2 text-xs text-muted-foreground">{t('access.users.replaceHint')}</p>
            </div>
          </div>
        </div>
      </FormDrawer>

      <ConfirmModal
        open={!!pendingConfirm}
        onOpenChange={(v) => !v && !assign.isPending && setPendingConfirm(null)}
        title={t('access.users.reassign.title')}
        description={pendingConfirm?.message || t('access.users.reassign.body')}
        confirmLabel={t('access.users.reassign.confirm')}
        loading={assign.isPending}
        onConfirm={() => void submit(true)}
      >
        <ul className="max-h-64 space-y-2 overflow-y-auto">
          {pendingConfirm?.affectedUsers.map((u) => (
            <li key={u.id} className="rounded-lg border px-3 py-2 text-sm">
              <p className="font-medium">{u.fullName}</p>
              <p className="text-xs text-muted-foreground">{u.email}</p>
              {u.currentRoleNames.length ? (
                <p className="mt-1 text-xs text-muted-foreground">{t('access.users.reassign.currentRoles', { names: u.currentRoleNames.join(', ') })}</p>
              ) : null}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-warning">{t('access.users.reassign.sessionHint')}</p>
      </ConfirmModal>
    </>
  )
}
